import React, { useState } from 'react';
import axios from 'axios';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const EnquiryForm = ({ compact = false }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    state: '',
    service: '',
    loanType: '',
    loanAmount: '',
    message: ''
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await axios.post('http://localhost:5000/api/enquiry', formData);
      if (response.status === 201) {
        setStatus({ type: 'success', message: t('enquiryPage.form.status.success') });
        setFormData({
          name: '', phone: '', email: '', city: '', state: '',
          service: '', loanType: '', loanAmount: '', message: ''
        });
      }
    } catch (error) {
      setStatus({ type: 'error', message: t('enquiryPage.form.status.error') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`bg-white rounded-2xl shadow-xl overflow-hidden border border-gold-primary/20 ${compact ? '' : 'max-w-4xl mx-auto'}`}>
      <div className="bg-dark-green py-6 px-8">
        <h3 className="text-2xl font-bold text-white">{t('enquiryPage.form.title')}</h3>
        <p className="text-gold-light text-sm mt-1">{t('enquiryPage.form.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-dark-green mb-1">{t('enquiryPage.form.fields.name')} *</label>
              <input
                required
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t('enquiryPage.form.placeholders.name')}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-gold-primary focus:border-transparent outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-dark-green mb-1">{t('enquiryPage.form.fields.phone')} *</label>
              <input
                required
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={t('enquiryPage.form.placeholders.phone')}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-gold-primary focus:border-transparent outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-dark-green mb-1">{t('enquiryPage.form.fields.email')}</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('enquiryPage.form.placeholders.email')}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-gold-primary focus:border-transparent outline-none transition-all"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-dark-green mb-1">{t('enquiryPage.form.fields.city')}</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder={t('enquiryPage.form.placeholders.city')}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-gold-primary focus:border-transparent outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-dark-green mb-1">{t('enquiryPage.form.fields.state')}</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  placeholder={t('enquiryPage.form.placeholders.state')}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-gold-primary focus:border-transparent outline-none transition-all"
                />
              </div>
            </div>
          </div>

          {/* Service Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-dark-green mb-1">{t('enquiryPage.form.fields.service')} *</label>
              <select
                required
                name="service"
                value={formData.service}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-gold-primary focus:border-transparent outline-none transition-all"
              >
                <option value="">{t('enquiryPage.form.placeholders.service')}</option>
                <option value="Loan">{t('services.categories.loanServices.title')}</option>
                <option value="Insurance">{t('services.categories.insuranceServices.title')}</option>
                <option value="Consultancy">{t('services.categories.businessConsultancy.title')}</option>
                <option value="GST">{t('services.categories.complianceServices.items.gst.name')}</option>
                <option value="ITR">{t('services.categories.complianceServices.items.itr.name')}</option>
                <option value="Other">{t('common.other')}</option>
              </select>
            </div>

            {formData.service === 'Loan' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-dark-green mb-1">{t('enquiryPage.form.fields.loanType')}</label>
                  <select
                    name="loanType"
                    value={formData.loanType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-gold-primary focus:border-transparent outline-none transition-all"
                  >
                    <option value="">{t('enquiryPage.form.placeholders.loanType')}</option>
                    <option value="Home">{t('services.categories.loanServices.items.home.name')}</option>
                    <option value="Personal">{t('services.categories.loanServices.items.personal.name')}</option>
                    <option value="Business">{t('services.categories.loanServices.items.business.name')}</option>
                    <option value="Property">{t('services.categories.loanServices.items.property.name')}</option>
                    <option value="Vehicle">{t('services.categories.loanServices.items.vehicle.name')}</option>
                    <option value="MSME">{t('services.categories.loanServices.items.msme.name')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-dark-green mb-1">{t('enquiryPage.form.fields.amount')}</label>
                  <input
                    type="text"
                    name="loanAmount"
                    value={formData.loanAmount}
                    onChange={handleChange}
                    placeholder={t('enquiryPage.form.placeholders.amount')}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-gold-primary focus:border-transparent outline-none transition-all"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-semibold text-dark-green mb-1">{t('enquiryPage.form.fields.message')}</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={formData.service === 'Loan' ? 2 : 5}
                placeholder={t('enquiryPage.form.placeholders.message')}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-gold-primary focus:border-transparent outline-none transition-all resize-none"
              ></textarea>
            </div>
          </div>
        </div>

        {status.message && (
          <div className={`mt-6 p-4 rounded-lg flex items-center space-x-3 ${status.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {status.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            <span className="font-medium">{status.message}</span>
          </div>
        )}

        <button
          disabled={loading}
          type="submit"
          className="mt-8 w-full bg-gold-primary hover:bg-gold-primary/90 text-white font-bold py-4 rounded-lg shadow-lg transition-all transform hover:-translate-y-1 active:scale-95 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <>
              <span>{t('enquiryPage.form.submit')}</span>
              <Send size={18} />
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default EnquiryForm;
